// component/Geospatial/Facilitymap.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { TYPE_COLORS } from './mockData/GeospatialData';
import { createMarkerIcon } from './MapMarkerIcon';
import MapLegend from './Maplegend';
import { mapPointOf } from './displayPoints';
import { getMapTile, hasCartoKey } from '../../services/mapTileLayers';

const THEME_STORAGE_KEY = 'nlims-geo-map-theme';

function loadStoredTheme() {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'light';
  } catch {
    return 'light';
  }
}

// Fix Leaflet default icon path broken by bundlers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const NIGERIA_CENTER = [9.5, 8.5];
const DEFAULT_ZOOM = 6;

const FALLBACK_TYPE_COLOR = '#6b7280';

// Clusters only group records at country / state zoom; from LGA level in,
// every record shows as its own legend marker.
const DISABLE_CLUSTERING_AT_ZOOM = 10;

// Cluster bubble: a ring split by legend type (each type's share in its own
// colour) around the count, so a mixed cluster shows what it holds instead
// of looking like a single type. Size scales with count.
function createClusterIcon(cluster) {
  const count = cluster.getChildCount();
  const counts = new Map();
  cluster.getAllChildMarkers().forEach((m) => {
    const type = m.options.facilityType;
    counts.set(type, (counts.get(type) || 0) + 1);
  });
  const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  let start = 0;
  const stops = ranked.map(([type, n]) => {
    const color = TYPE_COLORS[type] ?? FALLBACK_TYPE_COLOR;
    const end = start + (n / count) * 360;
    const stop = `${color} ${start}deg ${end}deg`;
    start = end;
    return stop;
  });
  const ring = `conic-gradient(${stops.join(',')})`;
  const color = TYPE_COLORS[ranked[0][0]] ?? FALLBACK_TYPE_COLOR;
  const size = count < 10 ? 36 : count < 50 ? 42 : 50;
  const title = ranked.map(([type, n]) => `${type || 'Unknown'}: ${n}`).join(' · ');
  return L.divIcon({
    html: `<div class="nlims-cluster" title="${title.replace(/"/g, '&quot;')}" style="--cluster-color:${color};--cluster-ring:${ring};width:${size}px;height:${size}px"><span>${count}</span></div>`,
    className: 'nlims-cluster-wrapper',
    iconSize: L.point(size, size),
  });
}

// Bring the selected record into view. If its marker is folded into a
// cluster, zoom just far enough for the cluster to release it.
function MapController({ selectedFacility, clusterRef, markersRef }) {
  const map = useMap();
  useEffect(() => {
    if (!selectedFacility) return;
    const marker = markersRef.current.get(selectedFacility.id);
    const point = marker ? marker.getLatLng() : mapPointOf(selectedFacility);
    if (!point) return;
    const cluster = clusterRef.current;
    if (marker && cluster && cluster.hasLayer(marker) && cluster.getVisibleParent(marker) !== marker) {
      cluster.zoomToShowLayer(marker);
      return;
    }
    map.flyTo([point.lat, point.lng], Math.max(map.getZoom(), 8), { duration: 1.2 });
  }, [selectedFacility, map, clusterRef, markersRef]);
  return null;
}

// Leave room for the legend (bottom-left) and the control stacks (right) so
// fitted markers never land underneath them.
const FIT_PADDING = { paddingTopLeft: [60, 50], paddingBottomRight: [70, 50] };
const SINGLE_POINT_ZOOM = 12;
const MAX_FIT_ZOOM = 11;

// Fly to a set of points: one point zooms straight in, several fit the
// viewport to all of them so every location stays visible.
function flyToPoints(map, points, { duration = 1.1 } = {}) {
  if (!points.length) return;
  map.stop();
  if (points.length === 1) {
    map.flyTo([points[0].lat, points[0].lng], SINGLE_POINT_ZOOM, { duration });
    return;
  }
  const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
  // All points in one spot (same site) — fitBounds would zoom to max, so treat as one.
  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
    map.flyTo(bounds.getCenter(), SINGLE_POINT_ZOOM, { duration });
    return;
  }
  map.flyToBounds(bounds, { ...FIT_PADDING, maxZoom: MAX_FIT_ZOOM, duration });
}

// When the active type changes (legend or filter pills), move the map to
// wherever that type's records are; clearing the type returns to Nigeria.
function TypeFocusController({ activeType, points, loading }) {
  const map = useMap();
  const lastFocused = useRef(undefined);
  useEffect(() => {
    if (loading) return;
    const key = activeType ?? null;
    const previous = lastFocused.current;
    if (previous === key) return;
    lastFocused.current = key;
    if (!key) {
      // Skip the initial mount; only fly back when a type was just cleared.
      if (previous !== undefined) map.flyTo(NIGERIA_CENTER, DEFAULT_ZOOM, { duration: 0.9 });
      return;
    }
    flyToPoints(map, points);
  }, [activeType, points, loading, map]);
  return null;
}

// Handles explicit "go to this group" requests from the legend's location list.
function FocusRequestController({ request }) {
  const map = useMap();
  useEffect(() => {
    if (request?.points?.length) flyToPoints(map, request.points);
  }, [request, map]);
  return null;
}

function MapInteractionController({ interactive }) {
  const map = useMap();
  useEffect(() => {
    const handlers = [
      map.dragging, map.scrollWheelZoom,
      map.doubleClickZoom, map.touchZoom, map.boxZoom,
    ];
    handlers.forEach((h) => h && (interactive ? h.enable() : h.disable()));
  }, [interactive, map]);
  return null;
}

function ZoomButtons() {
  const map = useMap();
  return (
    <div className="absolute bottom-5 right-4 z-[1000] flex flex-col gap-1">
      <button
        onClick={() => map.flyTo(NIGERIA_CENTER, DEFAULT_ZOOM, { duration: 0.8 })}
        className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-gray-50 transition-colors"
        title="Reset view"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" stroke="#374151" strokeWidth="2" />
          <polyline points="9,22 9,12 15,12 15,22" stroke="#374151" strokeWidth="2" />
        </svg>
      </button>
      <button
        onClick={() => map.locate({ setView: true, maxZoom: 10 })}
        className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-gray-50 transition-colors"
        title="Locate me"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="4" stroke="#374151" strokeWidth="2" />
          <path d="M12 2v4M12 18v4M2 12h4M18 12h4" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </button>
      <button
        onClick={() => map.zoomIn()}
        className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md text-gray-700 text-xl leading-none hover:bg-gray-50 transition-colors"
        title="Zoom in"
      >
        +
      </button>
      <button
        onClick={() => map.zoomOut()}
        className="w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md text-gray-700 text-xl leading-none hover:bg-gray-50 transition-colors"
        title="Zoom out"
      >
        -
      </button>
    </div>
  );
}

function MapThemeControls({ theme, onThemeChange }) {
  const btnBase =
    'w-8 h-8 bg-white border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer shadow-md hover:bg-gray-50 transition-colors';
  const activeBtn = '!bg-green-50 !border-green-400 text-green-700';

  return (
    <div className="absolute top-4 right-4 z-[1000] flex flex-col gap-1">
      <button
        onClick={() => onThemeChange('light')}
        title="Light map"
        className={`${btnBase} ${theme === 'light' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="5" />
          <path d="M12 1v2m0 18v2M4.22 4.22l1.42 1.42m12.72 12.72l1.42 1.42M1 12h2m18 0h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
        </svg>
      </button>
      <button
        onClick={() => onThemeChange('dark')}
        title="Dark map"
        className={`${btnBase} ${theme === 'dark' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z" />
        </svg>
      </button>
      <button
        onClick={() => onThemeChange('satellite')}
        title="Satellite view"
        className={`${btnBase} ${theme === 'satellite' ? activeBtn : 'text-gray-600'}`}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 2a10 10 0 100 20A10 10 0 0012 2z" />
          <path d="M2 12h20M12 2c-2.76 4-4 8-4 10s1.24 6 4 10M12 2c2.76 4 4 8 4 10s-1.24 6-4 10" />
        </svg>
      </button>
    </div>
  );
}

export default function FacilityMap({
  activeType,
  facilities = [],
  selectedFacility,
  onSelectFacility,
  onTypeClick,
  loading = false,
}) {
  const [mapInteractive, setMapInteractive] = useState(false);
  const [theme, setTheme] = useState(loadStoredTheme);
  const [focusRequest, setFocusRequest] = useState(null);
  const clusterRef = useRef(null);
  const markersRef = useRef(new Map());
  const tile = getMapTile(theme);

  // Records of the active type that actually have coordinates.
  const plotted = useMemo(
    () =>
      (Array.isArray(facilities) ? facilities : [])
        .filter((facility) => !activeType || facility.type === activeType)
        .map((facility) => ({ facility, point: mapPointOf(facility) }))
        .filter(({ point }) => point),
    [facilities, activeType]
  );
  const activePoints = useMemo(() => plotted.map(({ point }) => point), [plotted]);

  // Group the active type's locations by state for the legend's jump list.
  const locationGroups = useMemo(() => {
    if (!activeType) return [];
    const groups = new Map();
    plotted.forEach(({ facility, point }) => {
      const state = String(facility.state || '').trim() || 'Unspecified state';
      if (!groups.has(state)) groups.set(state, { state, points: [] });
      groups.get(state).points.push(point);
    });
    return [...groups.values()].sort((a, b) => a.state.localeCompare(b.state));
  }, [plotted, activeType]);

  // Picking from the legend should leave the map ready to pan/zoom straight away.
  const handleLegendTypeClick = (type) => {
    setMapInteractive(true);
    onTypeClick(type);
  };

  const handleFocusGroup = (points) => {
    setMapInteractive(true);
    setFocusRequest({ points });
  };

  const handleThemeChange = (next) => {
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // localStorage unavailable (private mode, etc.) — theme just won't persist.
    }
  };

  return (
    <div className="relative flex-1 min-h-0">
      <MapContainer
        center={NIGERIA_CENTER}
        zoom={DEFAULT_ZOOM}
        className="w-full h-full rounded-2xl"
        zoomControl={false}
        dragging={false}
        scrollWheelZoom={false}
        doubleClickZoom={false}
      >
        <TileLayer
          key={theme}
          url={tile.url}
          attribution={tile.attribution}
          subdomains="abcd"
          maxZoom={20}
        />

        <MarkerClusterGroup
          ref={clusterRef}
          chunkedLoading
          maxClusterRadius={45}
          disableClusteringAtZoom={DISABLE_CLUSTERING_AT_ZOOM}
          showCoverageOnHover={false}
          spiderfyOnMaxZoom
          iconCreateFunction={createClusterIcon}
        >
          {plotted.map(({ facility, point }) => (
            <Marker
              key={facility.id}
              ref={(marker) => {
                if (marker) markersRef.current.set(facility.id, marker);
                else markersRef.current.delete(facility.id);
              }}
              position={[point.lat, point.lng]}
              facilityType={facility.type}
              icon={createMarkerIcon(facility.type, selectedFacility?.id === facility.id)}
              eventHandlers={{ click: () => onSelectFacility(facility) }}
            />
          ))}
        </MarkerClusterGroup>

        <MapController selectedFacility={selectedFacility} clusterRef={clusterRef} markersRef={markersRef} />
        <TypeFocusController activeType={activeType} points={activePoints} loading={loading} />
        <FocusRequestController request={focusRequest} />
        <MapInteractionController interactive={mapInteractive} />
        <ZoomButtons />
        <MapThemeControls theme={theme} onThemeChange={handleThemeChange} />
      </MapContainer>

      {!hasCartoKey && theme !== 'satellite' && (
        <div className="absolute top-4 left-4 z-[1000] max-w-[260px] rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-[11px] font-medium text-amber-800 shadow-sm">
          Map tiles need a free CARTO API key. Add it to REACT_APP_CARTO_API_KEY in .env.local — get one at carto.com/basemaps/apikey.
        </div>
      )}

      {!mapInteractive && (
        <div
          onClick={() => setMapInteractive(true)}
          className="absolute inset-0 rounded-2xl z-[1001] flex items-center justify-center cursor-pointer"
          style={{ background: 'rgba(5,14,30,0.55)', backdropFilter: 'blur(3px)' }}
        >
          <div
            className="flex flex-col items-center gap-3 px-8 py-6 rounded-2xl"
            style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)' }}
          >
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
              <path d="M9 11V6a1.5 1.5 0 013 0v4.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M12 10.5V9a1.5 1.5 0 013 0v1.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M15 10.5a1.5 1.5 0 013 0v1" stroke="white" strokeWidth="1.8" strokeLinecap="round" />
              <path d="M9 11v4c0 2.5 2 4 4.5 4s4.5-1.5 4.5-4v-4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9 13c-1 0-2-.5-2-2V6a1.5 1.5 0 00-3 0v7c0 3.5 2.5 6 6 6" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.4" />
            </svg>
            <p className="text-white font-bold text-sm tracking-wide">Click to interact with map</p>
            <p className="text-white/50 text-xs">Pan, zoom, and select records</p>
          </div>
        </div>
      )}

      {loading && (
        <div className="absolute inset-0 z-[1002] flex items-center justify-center rounded-2xl bg-white/70 backdrop-blur-[1px]">
          <div className="rounded-full border border-green-200 bg-white px-4 py-2 text-sm font-medium text-green-700 shadow-sm">
            Loading geospatial records...
          </div>
        </div>
      )}

      <MapLegend
        activeType={activeType}
        onTypeClick={handleLegendTypeClick}
        locationGroups={locationGroups}
        totalPoints={activePoints.length}
        onFocusGroup={handleFocusGroup}
        theme={theme === 'light' ? 'light' : 'dark'}
      />

      <style>{`
        .facility-tooltip {
          background: rgba(15,23,42,0.92) !important;
          border: 1px solid rgba(255,255,255,0.1) !important;
          color: #fff !important;
          border-radius: 6px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          padding: 4px 10px !important;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3) !important;
        }
        .facility-tooltip::before { border-top-color: rgba(15,23,42,0.92) !important; }
        .leaflet-container { font-family: inherit; }
        .nlims-cluster-wrapper { background: transparent; border: none; }
        .nlims-cluster {
          display: flex; align-items: center; justify-content: center;
          border-radius: 9999px;
          background: var(--cluster-ring);
          border: 2px solid rgba(255,255,255,0.95);
          box-shadow: 0 0 0 4px color-mix(in srgb, var(--cluster-color) 30%, transparent), 0 2px 8px rgba(0,0,0,0.25);
          transition: transform 0.15s ease;
        }
        .nlims-cluster span {
          display: flex; align-items: center; justify-content: center;
          width: calc(100% - 10px); height: calc(100% - 10px);
          border-radius: 9999px; background: #fff;
          color: #111827; font-size: 12px; font-weight: 700;
        }
        .nlims-cluster:hover { transform: scale(1.08); }
      `}</style>
    </div>
  );
}
